import {
  convertDateToYYYYMMDDFormat,
  CustomDateRangePicker,
  NoDataComponent,
  PageWrapper,
  SimpleTableLoadingSkeleton,
  STATUSPAGE,
  TableVirtualized,
} from '@repo/ui';
import { useFormik } from 'formik';
import noData from 'presentation/app/assets/images/no-task.png';
import { makeActivitiesSummarizedByStudentColumns } from 'presentation/app/modules/Reports/common/columns/activities/activities-summarized-by-student-columns';
import { ReportsGeneralFilters } from 'presentation/app/modules/Reports/components/reports-general-filters';
import { useActivitiesSummarizedByStudent } from 'presentation/app/modules/Reports/hooks/useActivitiesSummarizedByStudent';
import { useState } from 'react';
import { Col, Card, Label, Spinner, Row } from 'reactstrap';
import * as Yup from 'yup';

export const ActivitiesSummarizedByStudent = () => {
  const [buttonSelectedSubmit, setButtonSelectedSubmit] = useState<string>();

  const {
    statusGet,
    hasFetchedData,
    summarizedByStudent,
    handleActivitiesSummarizedByStudentFile,
    requestActivitiesSummarizedByStudentJSON,
  } = useActivitiesSummarizedByStudent();

  function getCurrentDate() {
    return new Date();
  }

  function getFirstDayOfMonth() {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      entity: null,
      course: null,
      unit: null,
      semester: null,
      groups: [],
      starts: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
      ends: convertDateToYYYYMMDDFormat(getCurrentDate()),
    },
    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string().nullable(),
          value: Yup.string().nullable(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      starts: Yup.date().required('Data inicial é obrigatória.'),
      ends: Yup.date().required('Data final é obrigatória.'),
    }),

    onSubmit: (values) => {
      const groupsValues = values.groups.map((group) => group.value);

      const valuesToSubmit = {
        ends: values.ends,
        starts: values.starts,
        groups: groupsValues,
        curricularUnit: validation?.values?.unit?.value,
        entity: validation?.values?.entity?.value,
        course: validation?.values?.course?.value,
        semester: validation?.values?.semester?.value,
      };

      {
        buttonSelectedSubmit === 'json' &&
          requestActivitiesSummarizedByStudentJSON({
            ...valuesToSubmit,
            type: '2',
          });
      }
      {
        buttonSelectedSubmit === 'pdf' &&
          handleActivitiesSummarizedByStudentFile({
            ...valuesToSubmit,
            type: '0',
          });
      }
      {
        buttonSelectedSubmit === 'excel' &&
          handleActivitiesSummarizedByStudentFile({
            ...valuesToSubmit,
            type: '1',
          });
      }
    },
  });

  const formattedData = summarizedByStudent?.map((register, idx) => ({
    id: idx,
    ...register,
  }));

  return (
    <PageWrapper title="Resumido">
      <Col className="mb-3 border-0 d-flex flex-column">
        <h4>Resumido</h4>
        <p className="text-muted mb-0">
          Visualize o resumo de atividades por estudante.
        </p>
      </Col>

      <Card>
        <ReportsGeneralFilters
          validation={validation}
          inputsChildren={
            <Col sm={12} md={6} lg={3} className="mb-3">
              <Row>
                <Label htmlFor="initial-date" className="form-label">
                  Período
                </Label>
                <CustomDateRangePicker
                  placement="auto"
                  onDateChange={(start, ends) => {
                    validation.setFieldValue(
                      'starts',
                      convertDateToYYYYMMDDFormat(start),
                    );
                    validation.setFieldValue(
                      'ends',
                      convertDateToYYYYMMDDFormat(ends),
                    );
                  }}
                />
              </Row>
            </Col>
          }
          buttonsChildren={
            <div className="d-flex flex-wrap gap-2 justify-content-end align-content-end">
              <button
                id="json"
                type="submit"
                onClick={() => {
                  setButtonSelectedSubmit('json');
                }}
                className="btn btn-primary waves-effect waves-light"
                disabled={Boolean(statusGet)}
              >
                {buttonSelectedSubmit === 'json' && Boolean(statusGet) ? (
                  <Spinner size="sm">Carregando...</Spinner>
                ) : (
                  <>
                    Visualizar
                    <i className="ri-computer-line mx-2"></i>
                  </>
                )}
              </button>

              <button
                type="submit"
                id="pdf"
                onClick={() => {
                  setButtonSelectedSubmit('pdf');
                }}
                className="btn btn-light waves-effect waves-light"
                disabled={Boolean(statusGet)}
              >
                {buttonSelectedSubmit === 'pdf' && Boolean(statusGet) ? (
                  <Spinner size="sm">Carregando...</Spinner>
                ) : (
                  <>
                    Visualizar em PDF
                    <i className="las la-file-pdf mx-2"></i>
                  </>
                )}
              </button>
              <button
                id="excel"
                type="submit"
                onClick={() => {
                  setButtonSelectedSubmit('excel');
                }}
                className="btn btn-light waves-effect waves-light"
                disabled={Boolean(statusGet)}
              >
                {buttonSelectedSubmit === 'excel' && Boolean(statusGet) ? (
                  <Spinner size="sm">Carregando...</Spinner>
                ) : (
                  <>
                    Exportar para Excel
                    <i className="ri-file-excel-2-line mx-2"></i>
                  </>
                )}
              </button>
            </div>
          }
        />
        {summarizedByStudent?.length === 0 &&
          hasFetchedData &&
          statusGet === STATUSPAGE.DONE && (
            <div className="p-4">
              <NoDataComponent src={noData} />
            </div>
          )}

        {statusGet === STATUSPAGE.LOADING && <SimpleTableLoadingSkeleton />}

        {statusGet === STATUSPAGE.DONE && summarizedByStudent?.length > 0 && (
          <TableVirtualized
            columns={makeActivitiesSummarizedByStudentColumns()}
            data={formattedData}
          />
        )}
      </Card>
    </PageWrapper>
  );
};
